import React from 'react';
import { showToast } from '@ditioas/web-ui';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { SWRConfiguration } from 'swr';
import { mutate } from 'swr';

import config from 'config/site.config';
import type { AlertFormShape } from 'containers/Alerts/Alert/utils';
import useAlertsFilters from 'containers/Alerts/AlertsList/AlertsFilters/useAlertsFilters';
import type { AlertTypeFormShape } from 'containers/Alerts/AlertTypes/AlertTypeForm/utils';
import { useApi, usePostSWRFetcher } from 'data/base-api';
import useCurrentUserAndSettings from 'hooks/useCurrentUserAndSettings';
import useHandleError from 'hooks/useHandleError';
import useObjectSearchParams from 'hooks/useObjectSearchParams';
import useShowAlert from 'hooks/useShowAlert';
import { checkStatus, del, get, patch, post, put } from 'libs/apiClient';
import { localeCompare } from 'libs/sortFunctions';
import type {
  AlertsProjectsResponse,
  AssignedDetails,
  CompanyAlert,
  CompanyAlertType,
  CompanySettings,
  HMSRegAlertType,
  MachineAlert,
  NewProjectAlertType,
  ProjectAlertType,
  ProjectSettings,
  SeverityLevel,
} from 'model/Alerts/types';
import {
  AlertStatus,
  ProjectNotificationAccessLevel,
  ProjectsAlertsTabs,
} from 'model/Alerts/types';

export const alertsBaseUrl = '/api/v2/proj-notifications';
const settingsBaseUrl = '/api/settings';
const companyTypesBaseUrl = `${alertsBaseUrl}/types-company`;

const {
  modules: { alerts: alertsUrls },
} = config;

export const useGetAlertsProjects = (revalidateOnMount = true) => {
  const { data, ...rest } = useApi<AlertsProjectsResponse>(
    `${alertsBaseUrl}/projects`,
    { revalidateOnMount }
  );

  const sortedData = React.useMemo(
    () =>
      data
        ? {
            ...data,
            projects: [...data.projects].sort((a, b) =>
              localeCompare(
                a.projectName.trim().toLocaleLowerCase(),
                b.projectName.trim().toLocaleLowerCase()
              )
            ),
          }
        : data,
    [data]
  );

  return { data: sortedData, ...rest };
};

export const useGetCompanyAlertTypes = (
  excludeMachineTypes = false,
  onlyMachineTypes = false,
  skip = false,
  SWRConfig?: SWRConfiguration
) => {
  const { data: companyAlertTypes, ...rest } = useApi<CompanyAlertType[]>(
    skip
      ? null
      : `${companyTypesBaseUrl}${
          excludeMachineTypes ? '?excludeMachine=true' : ''
        }${onlyMachineTypes ? '?onlyMachine=true' : ''}`,
    SWRConfig
  );

  return { companyAlertTypes, ...rest };
};

export const useGetMachineAlertTypes = (
  machineId: string,
  skip = false,
  SWRConfig?: SWRConfiguration
) =>
  useApi<ProjectAlertType[]>(
    // eslint-disable-next-line no-nested-ternary
    !skip
      ? machineId
        ? `${alertsBaseUrl}/types/machine?machineId=${machineId}`
        : `${alertsBaseUrl}/types/machine`
      : null,
    SWRConfig
  );

export const useGetHMSRegAlertTypes = (
  customerId: string | null,
  skip = false,
  revalidateOnMount = true
) => {
  const { data, ...rest } = usePostSWRFetcher<HMSRegAlertType[]>(
    !customerId || skip
      ? null
      : `${alertsBaseUrl}/notifications/external-types`,
    {
      CustomerID: customerId,
    },
    { revalidateOnMount }
  );

  // postFetcher parses error bodies as data, so a failed lookup yields an
  // error object here instead of the expected array
  return {
    hmsRegAlertTypes: Array.isArray(data) ? data : undefined,
    ...rest,
  };
};

export const useCreateCompanyAlertType = () => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserAndSettings();

  const { showSuccessMessage } = useShowAlert();
  const handleError = useHandleError();

  return async (
    newCompanyAlertType: AlertTypeFormShape
  ): Promise<CompanyAlertType | null> => {
    let newType: CompanyAlertType | null = null;

    await mutate<CompanyAlertType[]>(
      (key) => typeof key === 'string' && key.startsWith(companyTypesBaseUrl),
      async (types) => {
        try {
          const response = checkStatus(
            await post(`${alertsBaseUrl}/types`, newCompanyAlertType)
          );

          showSuccessMessage({
            message: t('Alerts.CompanyTypeSettings.AlertTypeCreated'),
          });

          const parsed = await response.json();
          newType = parsed;

          if (parsed) {
            return [...(types || []), parsed];
          }

          return types;
        } catch (e) {
          handleError(e);

          return types;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) => [
          ...(data || []),
          {
            ...(newCompanyAlertType as CompanyAlertType),
            id: 'placeholder',
            companyId: currentUser.companyId,
            projectId: null,
            modifiedAt: new Date().toISOString(),
            modifiedByUserId: currentUser.id,
            customCategory: [],
            customCategoryPropertyName: null,
            forbiddenBaseTypes: [],
          },
        ],
      }
    );

    return newType;
  };
};

export const useUpdateCompanyAlertType = () => {
  const { t } = useTranslation();

  const { showSuccessMessage } = useShowAlert();
  const handleError = useHandleError();

  return async (
    newCompanyAlertType: CompanyAlertType
  ): Promise<CompanyAlertType | null> => {
    let newType: CompanyAlertType | null = null;

    await mutate<CompanyAlertType[]>(
      (key) => typeof key === 'string' && key.startsWith(companyTypesBaseUrl),
      async (types) => {
        try {
          const response = checkStatus(
            await put(`${alertsBaseUrl}/types`, newCompanyAlertType)
          );

          showSuccessMessage({
            message: t('Alerts.CompanyTypeSettings.AlertTypeUpdated'),
          });

          const parsed = await response.json();
          newType = parsed;

          if (parsed) {
            return (
              types?.map((type) =>
                type.id === newCompanyAlertType.id ? parsed : type
              ) || [parsed]
            );
          }

          return types;
        } catch (e) {
          handleError(e);

          return types;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) =>
          data
            ? data.map((type) =>
                type.id === newCompanyAlertType.id ? newCompanyAlertType : type
              )
            : [newCompanyAlertType],
      }
    );

    return newType;
  };
};

export const useDeleteCompanyAlertType = () => {
  const { t } = useTranslation();

  const { showSuccessMessage } = useShowAlert();
  const handleError = useHandleError();

  return async (companyTypeId: string) => {
    await mutate<CompanyAlertType[]>(
      (key) => typeof key === 'string' && key.startsWith(companyTypesBaseUrl),
      async (types) => {
        try {
          checkStatus(await del(`${alertsBaseUrl}/types/${companyTypeId}`));

          showSuccessMessage({
            message: t('Alerts.CompanyTypeSettings.AlertTypeDeleted'),
          });

          return types?.filter((type) => type.id !== companyTypeId) || [];
        } catch (e) {
          handleError(e);

          return types;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) =>
          data?.filter((type) => type.id !== companyTypeId) || [],
      }
    );
  };
};

export const useGetCompanySettings = () => {
  const { data: companySettings, ...rest } = useApi<CompanySettings>(
    `${settingsBaseUrl}/company-settings`,
    { errorRetryCount: 0 },
    true
  );

  return { companySettings, ...rest };
};

export const useCreateCompanySettings = () => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserAndSettings();

  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    newCompanySettings: Partial<
      Pick<
        CompanySettings,
        'receiversOfAllNotifications' | 'receiversOfAllChecklists'
      >
    >
  ) => {
    mutate<CompanySettings>(
      `${settingsBaseUrl}/company-settings`,
      async (data) => {
        try {
          const response = checkStatus(
            await post(
              `${settingsBaseUrl}/company-settings`,
              newCompanySettings
            )
          );

          showSuccessMessage({
            message: t('CompanySettings.CompanySettingsCreated'),
          });

          const parsed = (await response.json()) as CompanySettings;

          return parsed;
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) => ({
          ...(data || {
            id: 'placeholder',
            companyId: currentUser.companyId,
            recVersion: 0,
            companyIds: [currentUser.companyId],
          }),
          receiversOfAllNotifications:
            newCompanySettings.receiversOfAllNotifications ?? [],
          receiversOfAllChecklists:
            newCompanySettings.receiversOfAllChecklists ?? [],
        }),
      }
    );
  };
};

export const useUpdateCompanySettings = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (newCompanySettings: CompanySettings) => {
    mutate<CompanySettings>(
      `${settingsBaseUrl}/company-settings`,
      async (data) => {
        try {
          const response = checkStatus(
            await put(`${settingsBaseUrl}/company-settings`, newCompanySettings)
          );

          showSuccessMessage({
            message: t('CompanySettings.CompanySettingsUpdated'),
          });

          const parsed = (await response.json()) as CompanySettings;

          return parsed;
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: () => newCompanySettings,
      }
    );
  };
};

export const useGetProjectAlertTypes = (
  projectId: string,
  includeInactive = true,
  revalidateOnMount = true,
  skip = false
) => {
  const { data: projectAlertTypes, ...rest } = useApi<ProjectAlertType[]>(
    !skip && projectId
      ? `${alertsBaseUrl}/types/${projectId}?includeInactive=${includeInactive}&includeTypesNotAvailableOnApp=true`
      : null,
    { revalidateOnMount },
    true
  );

  return { projectAlertTypes, ...rest };
};

export const useGetProjectAlertTypeSettings = (
  projectId: string,
  includeInactive = true,
  revalidateOnMount = true,
  skip = false
) => {
  const { data: projectAlertTypes, ...rest } = useApi<ProjectAlertType[]>(
    !skip && projectId
      ? `${alertsBaseUrl}/types/settings/${projectId}?includeInactive=${includeInactive}&includeTypesNotAvailableOnApp=true`
      : null,
    { revalidateOnMount },
    true
  );

  return { projectAlertTypes, ...rest };
};

// The POST/PUT /types-project responses return the override, not the merged
// read model, so the settings list is revalidated instead of patched.
export const revalidateProjectAlertTypeSettings = (projectId: string) =>
  mutate(
    (key) =>
      typeof key === 'string' &&
      key.startsWith(`${alertsBaseUrl}/types/settings/${projectId}`)
  );

export const useCreateProjectAlertType = () => {
  const { t } = useTranslation();
  const { showSuccessMessage } = useShowAlert();
  const handleError = useHandleError();

  return async (
    newProjectAlertType: NewProjectAlertType
  ): Promise<Omit<
    NewProjectAlertType,
    'projectNotificationTypeParentId'
  > | null> => {
    try {
      const response = checkStatus(
        await post(`${alertsBaseUrl}/types-project`, newProjectAlertType)
      );

      showSuccessMessage({
        message: t('Alerts.ProjectAlertsSetupCreated'),
      });

      const parsed = await response.json();
      return parsed;
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useUpdateProjectAlertType = () => {
  const { t } = useTranslation();
  const { showSuccessMessage } = useShowAlert();
  const handleError = useHandleError();

  return async (
    newProjectAlertType: NewProjectAlertType
  ): Promise<Omit<
    NewProjectAlertType,
    'projectNotificationTypeParentId'
  > | null> => {
    try {
      const response = checkStatus(
        await put(`${alertsBaseUrl}/types-project`, newProjectAlertType)
      );

      showSuccessMessage({
        message: t('Alerts.ProjectAlertsSetupUpdated'),
      });

      const parsed = await response.json();
      return parsed;
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

interface Filters {
  projectIds?: string[];
  status?: ProjectsAlertsTabs;
  fromDate?: string;
  toDate?: string;
  risk?: SeverityLevel;
  typeId?: string;
  assignedToId?: string;
  createdById?: string;
  categoryId?: string;
  relatedToId?: string;
  causeId?: string;
  involvedPartiesId?: string;
  requiresFurtherAction?: boolean;
  machineId?: string;
  onlyAssignedToCurrentUser?: boolean;
}

export const useGetProjectSettings = (projectId: string) => {
  const { data: projectSettings, ...rest } = useApi<ProjectSettings>(
    projectId ? `${settingsBaseUrl}/project-settings/${projectId}` : null,
    { errorRetryCount: 0 },
    true
  );

  return { projectSettings, ...rest };
};

export const useCreateProjectSettings = () => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserAndSettings();

  const handleError = useHandleError();

  return async (
    newProjectSettings: Pick<
      ProjectSettings,
      'projectId' | 'receiversOfAllNotifications' | 'receiversOfAllChecklists'
    >
  ) => {
    mutate<ProjectSettings>(
      `${settingsBaseUrl}/project-settings/${newProjectSettings.projectId}`,
      async (data) => {
        try {
          const response = checkStatus(
            await post(
              `${settingsBaseUrl}/project-settings`,
              newProjectSettings
            )
          );

          showToast(t('Alerts.ProjectAlertsSetupCreated'));

          const settings: ProjectSettings = await response.json();

          return settings;
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) => {
          const newData = data || {
            id: 'placeholder',
            companyId: currentUser.companyId,
            recVersion: 0,
            companyIds: [currentUser.companyId],
          };

          return {
            ...newData,
            projectId: newProjectSettings.projectId,
            receiversOfAllChecklists:
              newProjectSettings.receiversOfAllChecklists,
            receiversOfAllNotifications:
              newProjectSettings.receiversOfAllNotifications,
          };
        },
      }
    );
  };
};

export const useUpdateProjectSettings = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();

  return async (newProjectSettings: ProjectSettings) => {
    mutate<ProjectSettings>(
      `${settingsBaseUrl}/project-settings/${newProjectSettings.projectId}`,
      async (data) => {
        try {
          const response = checkStatus(
            await put(`${settingsBaseUrl}/project-settings`, newProjectSettings)
          );

          showToast(t('Alerts.ProjectAlertsSetupUpdated'));

          const settings: ProjectSettings = await response.json();

          return settings;
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) => {
          const newData = { ...(data as ProjectSettings) };

          return {
            ...newData,
            projectId: newProjectSettings.projectId,
            receiversOfAllChecklists:
              newProjectSettings.receiversOfAllChecklists,
            receiversOfAllNotifications:
              newProjectSettings.receiversOfAllNotifications,
          };
        },
      }
    );
  };
};

export const useGetRelevantAlerts = (onlyAssignedToCurrentUser?: boolean) => {
  const alertFilters = useAlertsFilters();
  const {
    alertsForCurrentUser,
    alertsForMachines,
    projectIds,
    status,
    fromDate,
    toDate,
    assignedToId,
    submittedById,
    resourceId,
    machineId,
    needsFollowUp,
    risk,
    typeId,
    categoryId,
    relatedToId,
    causeId,
    involvedPartiesId,
  } = alertFilters;

  const filters = React.useMemo(() => {
    const newFilters: Filters = {
      projectIds,
      status: status !== ProjectsAlertsTabs.All ? status : undefined,
      fromDate:
        fromDate && toDate ? formatISO(startOfDay(fromDate)) : undefined,
      toDate: fromDate && toDate ? formatISO(endOfDay(toDate)) : undefined,
      assignedToId,
      createdById: submittedById,
      machineId: resourceId || machineId,
      requiresFurtherAction: needsFollowUp,
      risk,
      typeId,
      categoryId,
      relatedToId,
      causeId,
      involvedPartiesId,
      onlyAssignedToCurrentUser,
    };

    return newFilters;
  }, [alertFilters, alertsForCurrentUser, alertsForMachines]);

  return usePostSWRFetcher<CompanyAlert[] | MachineAlert[]>(
    alertsForMachines
      ? `${alertsBaseUrl}/notifications/machines`
      : `${alertsBaseUrl}/notifications/list`,
    filters
  );
};

export const useCreateAlert = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    newAlert: Partial<AlertFormShape | CompanyAlert>
  ): Promise<CompanyAlert | null> => {
    try {
      const response = checkStatus(
        await post(`${alertsBaseUrl}/notifications`, newAlert)
      );

      showSuccessMessage({
        message: t('Alerts.AlertCreated'),
      });

      const parsed = await response.json();
      return parsed;
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useUpdateAlert = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (newAlert: CompanyAlert, skipToast = false) => {
    let updatedAlert: CompanyAlert | null = null;

    await mutate<CompanyAlert>(
      `${alertsBaseUrl}/notification/${newAlert.id}`,
      async (data) => {
        try {
          const response = checkStatus(
            await put(`${alertsBaseUrl}/notifications`, newAlert)
          );

          if (!skipToast) {
            showSuccessMessage({
              message: t('Alerts.AlertUpdated'),
            });
          }

          const parsed = (await response.json()) as CompanyAlert;

          if (
            parsed.currentUserAccessLevel !==
            ProjectNotificationAccessLevel.NoAccess
          ) {
            updatedAlert = {
              ...data,
              ...parsed,
              history: newAlert.history || data?.history,
            } as CompanyAlert;

            return updatedAlert;
          }

          return data;
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      {
        revalidate: false,
      }
    );

    return updatedAlert as CompanyAlert | null;
  };
};

export const useDeleteAlert = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();

  return async (alertId: string): Promise<void> => {
    try {
      checkStatus(await del(`${alertsBaseUrl}/notification/${alertId}`));

      showToast(t('Alerts.AlertsDeleted', { count: 1 }));
    } catch (e) {
      handleError(e);
    }
  };
};

export const useBulkDeleteAlerts = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();

  return async (notificationIds: string[]): Promise<void> => {
    try {
      checkStatus(
        await del(`${alertsBaseUrl}/notifications/bulk-delete`, {
          notificationIds,
        })
      );

      showToast(t('Alerts.AlertsDeleted', { count: notificationIds.length }));
    } catch (e) {
      handleError(e);
    }
  };
};

export const useGetAlertById = (
  alertId?: string,
  skip = false,
  revalidateOnMount = true
) => {
  const { data, ...rest } = useApi<CompanyAlert>(
    alertId && !skip ? `${alertsBaseUrl}/notification/${alertId}` : null,
    { revalidateOnMount }
  );

  return {
    alert: data,
    ...rest,
  };
};

export const useGetPublicAlertById = (
  alertId: string | undefined,
  xai: string | undefined,
  xat: string | undefined
) => {
  const params = new URLSearchParams();
  if (xai) params.set('xai', xai);
  if (xat) params.set('xat', xat);

  const { data, ...rest } = useApi<CompanyAlert>(
    alertId && xai && xat
      ? `${alertsBaseUrl}/public-notification/${alertId}?${params}`
      : null
  );

  return {
    alert: data,
    ...rest,
  };
};

/**
 * Builds the absolute URL for downloading the branded alert PDF via the
 * anonymous, tokenized endpoint (same xai/xat token the public read view uses).
 * Backend route shipped in ditioas/timestyr#3445. Note: tokens minted before
 * that deploy don't carry the /pdf URL and get a 401 (the read view keeps
 * working) — callers must handle the failure gracefully.
 */
export const getPublicAlertPdfUrl = (
  alertId: string,
  xai: string,
  xat: string
) => {
  const params = new URLSearchParams();
  params.set('xai', xai);
  params.set('xat', xat);

  return `${alertsBaseUrl}/public-notification/${alertId}/pdf?${params}`;
};

export const useAssignSingleAlert = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();

  return async (alert: CompanyAlert, userIds: string[]) => {
    await mutate<CompanyAlert>(
      `${alertsBaseUrl}/notification/${alert.id}`,
      async (data) => {
        try {
          const response = checkStatus(
            await post(`${alertsBaseUrl}/notifications/assign`, {
              notificationIds: [alert.id],
              assignedToIds: userIds,
            })
          );

          showToast(t('Alerts.AssigneesUpdated'));

          const parsed = (await response.json()) as CompanyAlert[];

          if (parsed[0]) {
            return {
              ...parsed[0],
              history: parsed[0].history || data?.history,
              onlyResetMetaAndStatus: true,
            };
          }

          return data;
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      {
        revalidate: false,
      }
    );
  };
};

export const useSendAssignmentReminder = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();

  return async (alert: CompanyAlert) => {
    try {
      checkStatus(
        await post(`${alertsBaseUrl}/notifications/assignment-reminder`, {
          notificationIds: [alert.id],
        })
      );

      showToast(t('Alerts.ReminderSent'));
    } catch (e) {
      handleError(e);
    }
  };
};

export const useAssignMultipleAlerts = () => {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { projectIds, projectId } = useObjectSearchParams<{
    projectIds: string;
    projectId: string;
  }>();

  const handleError = useHandleError();

  return async (newAssignedTo: AssignedDetails, shouldNavigate?: boolean) => {
    if (newAssignedTo.notificationIds.length === 1) {
      await mutate(
        `${alertsBaseUrl}/notification/${newAssignedTo.notificationIds[0]}`,
        async (data) => {
          try {
            const response = checkStatus(
              await post(`${alertsBaseUrl}/notifications/assign`, newAssignedTo)
            );

            showToast(t('Alerts.AssigneesUpdated'));

            const parsed = await response.json();

            if (
              shouldNavigate &&
              parsed?.some(
                (a) =>
                  a.currentUserAccessLevel ===
                  ProjectNotificationAccessLevel.NoAccess
              )
            ) {
              if (pathname.includes(alertsUrls.userAlertsUrl)) {
                navigate(alertsUrls.userAlertsUrl);
              } else if (pathname.includes(alertsUrls.companyAlertsUrl)) {
                navigate(alertsUrls.companyAlertsUrl);
              } else if (projectId) {
                navigate(`${alertsUrls.listUrl}?projectIds=${projectId}`);
              } else {
                navigate(`${alertsUrls.listUrl}?projectIds=${projectIds}`);
              }
            }

            return {
              ...parsed,
              history: parsed.history || data?.history,
              onlyResetMetaAndStatus: true,
            };
          } catch (e) {
            handleError(e);

            return data;
          }
        },
        {
          revalidate: false,
        }
      );
    } else {
      checkStatus(
        await post(`${alertsBaseUrl}/notifications/assign`, newAssignedTo)
      );
    }
  };
};

export const downloadAlertsCSV = async (
  notificationIds: string[],
  projectIds?: string[],
  fromDate?: string | null,
  toDate?: string | null,
  userId?: string,
  machineCsv?: boolean,
  machineId?: string
): Promise<Blob> => {
  const response = checkStatus(
    await post(`${alertsBaseUrl}/notifications/download`, {
      notificationIds,
      fromDate: fromDate?.length ? fromDate : undefined,
      toDate: toDate?.length ? toDate : undefined,
      ...(userId ? { assignedToId: userId } : { projectIds }),
      ...(machineCsv ? { machineCsv } : {}),
      ...(machineId ? { machineId } : {}),
    })
  );
  return response.blob();
};

export const useDownloadAlertPdfs = () => {
  const handleError = useHandleError();

  return async (
    notificationIds: string[]
  ): Promise<{ file: File; filename: string } | null> => {
    try {
      const response = checkStatus(
        await post(`${alertsBaseUrl}/notifications/download-as-pdf`, {
          notificationIds,
        })
      );
      const header = response.headers.get('Content-Disposition');
      const regex = /filename\*=UTF-8''(.+)/gm;
      const matched = regex.exec(header)![1];
      const file = await response.blob();

      return { file, filename: decodeURIComponent(matched) };
    } catch (e) {
      handleError(e);

      return null;
    }
  };
};

export const useSetReadyForIntegration = () => {
  const handleError = useHandleError();

  return async (
    alertIds: string[],
    hmsRegType: string | null,
    feedbackToUser: string
  ) => {
    try {
      checkStatus(
        await patch(`${alertsBaseUrl}/notifications/ready-for-integration`, {
          notificationIds: alertIds,
          hmsRegType,
          feedbackToUser,
        })
      );
    } catch (e) {
      handleError(e);
    }
  };
};

export const useResolveAlert = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (alert: CompanyAlert, feedbackToUser: string) => {
    await mutate(
      `${alertsBaseUrl}/notification/${alert.id}`,
      async (data) => {
        try {
          const response = checkStatus(
            await patch(`${alertsBaseUrl}/notifications/resolve`, {
              notificationIds: [alert.id],
              feedbackToUser,
            })
          );

          showSuccessMessage({
            message: t('Alerts.Resolved'),
          });

          const parsed = (await response.json()) as CompanyAlert[];

          if (parsed[0]) {
            return {
              ...parsed[0],
              history: parsed[0].history || alert.history || data?.history,
            };
          }

          return data;
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      {
        optimisticData: (data) => ({
          ...(data || alert),
          status: AlertStatus.Closed,
        }),
        revalidate: false,
      }
    );
  };
};

export const useReopenExportedAlert = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (alert: CompanyAlert) => {
    await mutate(
      `${alertsBaseUrl}/notification/${alert.id}`,
      async (data) => {
        try {
          const response = checkStatus(
            await post(`${alertsBaseUrl}/notifications/reopen-exported`, {
              notificationIds: [alert.id],
            })
          );

          showSuccessMessage({
            message: t('Alerts.Reopened'),
          });

          const parsed = (await response.json()) as CompanyAlert[];

          if (parsed[0]) {
            return {
              ...parsed[0],
              history: parsed[0].history || alert.history || data?.history,
            };
          }

          return data;
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      {
        optimisticData: (data) => ({
          ...(data || alert),
          status: AlertStatus.InProgress,
          readyForExportToExternal: false,
        }),
        revalidate: false,
      }
    );
  };
};

export const useGetAlertShareUrl = () => {
  const handleError = useHandleError();

  return async (alertId: string): Promise<string | null> => {
    try {
      const response = checkStatus(
        await get(`${alertsBaseUrl}/notification/${alertId}/report-url`)
      );

      const url = await response.json();
      return url;
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};
